import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Trash2, Edit2, Copy, RefreshCw, FlaskConical, Target, CheckCircle, Lock, Unlock, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ProtectedView from '../components/ProtectedView';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function RnDChains() {
  const { user } = useAuth();
  const canViewPrices = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager';
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeChain, setActiveChain] = useState(null);
  const [materials, setMaterials] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/rnd-chains`, { withCredentials: true }),
        axios.get(`${API}/raw-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setChains(c.data || []);
      setMaterials(m.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (unlocked) load(); }, [unlocked]);

  const verify = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/rnd/verify-password`, { password: pw }, { withCredentials: true });
      setUnlocked(true);
      toast.success('R&D module unlocked');
    } catch { toast.error('Wrong password'); }
    finally { setVerifying(false); }
  };

  const [showPw, setShowPw] = useState(false);

  if (!unlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm w-full">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-amber-400" />
            <h2 className="font-heading text-lg text-zinc-50">R&D Access</h2>
          </div>
          <p className="text-zinc-500 text-xs mb-4">Recipe development data is confidential. Enter the secondary password.</p>
          <div className="relative mb-3">
            <input type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && verify()}
              placeholder="Secondary password"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-3 pr-10 py-2 text-zinc-50 text-sm" data-testid="rndchain-password" />
            <button type="button" onClick={() => setShowPw(v => !v)} data-testid="rndchain-toggle-pw"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-amber-400" title={showPw ? 'Hide' : 'Show'}>
              {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
          <button onClick={verify} disabled={verifying} data-testid="rndchain-unlock-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2 rounded-lg text-xs disabled:opacity-50">
            <Unlock size={13} /> {verifying ? 'Verifying…' : 'Unlock'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProtectedView label="R&D RECIPES · CONFIDENTIAL">
    <div className="space-y-5" data-testid="rnd-chains-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2"><FlaskConical size={18} className="text-amber-400" /> Recipe Development Chains</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Each chain tracks iterative trials (t1 → t2 → t3…) for a specific client & goal.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800"><RefreshCw size={13} /></button>
          <button onClick={() => setShowNew(true)} data-testid="new-chain-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            <Plus size={12} /> New Chain
          </button>
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm animate-pulse">Loading…</div>}

      <div className="grid md:grid-cols-2 gap-3">
        {chains.map(c => (
          <div key={c.id} onClick={() => setActiveChain(c)} data-testid={`chain-card-${c.id}`}
            className="bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 rounded-xl p-4 cursor-pointer transition-all">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-zinc-50 font-semibold truncate">{c.name}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">for {c.for_client || '—'} · {c.trial_count} trial{c.trial_count !== 1 ? 's' : ''}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${c.status === 'approved' ? 'bg-green-900/40 text-green-300' : c.status === 'archived' ? 'bg-zinc-800 text-zinc-500' : 'bg-amber-900/30 text-amber-300'}`}>{c.status.replace('_', ' ')}</span>
            </div>
            {c.goal && <div className="text-[12px] text-zinc-400 mt-1"><Target size={10} className="inline mr-1 text-amber-400" />{c.goal}</div>}
            {c.control_sample && <div className="text-[11px] text-zinc-500 mt-1 italic">vs {c.control_sample}</div>}
            {canViewPrices && c.target_cost_kg > 0 && <div className="text-[11px] text-emerald-300 mt-1">Target: {fmt(c.target_cost_kg)}/kg</div>}
          </div>
        ))}
        {chains.length === 0 && !loading && (
          <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-zinc-500 text-sm">
            No chains yet. Click <span className="text-amber-300">New Chain</span> to start recipe development.
          </div>
        )}
      </div>

      {showNew && <NewChainModal materials={materials} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {activeChain && <ChainDetailModal chainId={activeChain.id} materials={materials} canViewPrices={canViewPrices} onClose={() => { setActiveChain(null); load(); }} />}
    </div>
    </ProtectedView>
  );
}

function NewChainModal({ materials, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', for_client: '', goal: '', control_sample: '', target_taste: '', target_texture: '', target_color: '', target_cost_kg: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    axios.get(`${API}/debtors`, { withCredentials: true })
      .then(r => setClients(r.data || []))
      .catch(() => axios.get(`${API}/clients`, { withCredentials: true }).then(r => setClients(r.data || [])).catch(() => {}));
  }, []);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/rnd-chains`, { ...form, target_cost_kg: parseFloat(form.target_cost_kg) || 0 }, { withCredentials: true });
      toast.success('Chain created');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="New Recipe Chain" testid="new-chain-modal">
      <Input label="Chain Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testid="chain-name" placeholder="e.g. Peri Peri v2 Development" required />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-zinc-400 mb-1">For Client <span className="text-zinc-600">(from Debtors)</span></label>
          <input
            value={form.for_client}
            onChange={e => setForm(f => ({ ...f, for_client: e.target.value }))}
            list="chain-client-options"
            data-testid="chain-for-client"
            placeholder="Pick from debtors or type…"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
          />
          <datalist id="chain-client-options">
            {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </datalist>
        </div>
        <Input label="Target Cost / kg (₹)" type="number" value={form.target_cost_kg} onChange={v => setForm(f => ({ ...f, target_cost_kg: v }))} testid="chain-target-cost" />
      </div>
      <Input label="Goal (flavour profile, dietary constraints)" value={form.goal} onChange={v => setForm(f => ({ ...f, goal: v }))} testid="chain-goal" placeholder="e.g. medium heat, low sodium, vegan" />
      <Input label="Control / Sample to Match" value={form.control_sample} onChange={v => setForm(f => ({ ...f, control_sample: v }))} testid="chain-control-sample" placeholder="e.g. Nando's Peri Peri jar 125g" />
      <div className="grid grid-cols-3 gap-3">
        <Input label="Target Taste" value={form.target_taste} onChange={v => setForm(f => ({ ...f, target_taste: v }))} testid="chain-target-taste" placeholder="smoky, tangy" />
        <Input label="Target Texture" value={form.target_texture} onChange={v => setForm(f => ({ ...f, target_texture: v }))} testid="chain-target-texture" placeholder="fine powder" />
        <Input label="Target Colour" value={form.target_color} onChange={v => setForm(f => ({ ...f, target_color: v }))} testid="chain-target-color" placeholder="deep red" />
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={onClose} className="px-4 py-2 text-xs bg-zinc-800 text-zinc-300 rounded-lg">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="chain-save"
          className="px-4 py-2 text-xs bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Create Chain'}</button>
      </div>
    </Modal>
  );
}

function ChainDetailModal({ chainId, materials, canViewPrices, onClose }) {
  const [chain, setChain] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/rnd-chains/${chainId}`, { withCredentials: true });
      setChain(r.data);
    } catch { toast.error('Failed to load chain'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chainId]);

  const addTrial = async (cloneFromLabel = null) => {
    setBusy(true);
    try {
      const body = cloneFromLabel ? { clone_from: cloneFromLabel } : {};
      await axios.post(`${API}/rnd-chains/${chainId}/trials`, body, { withCredentials: true });
      toast.success('Trial added');
      load();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const updateTrial = async (tid, patch) => {
    try {
      await axios.patch(`${API}/rnd-chains/${chainId}/trials/${tid}`, patch, { withCredentials: true });
      load();
    } catch { toast.error('Update failed'); }
  };

  const delTrial = async (tid) => {
    const t = (chain?.trials || []).find(x => x.id === tid);
    const label = t?.label || 'this trial';
    if (!window.confirm(`Delete version "${label}" entirely?\n\nThis removes the column + all its ingredient quantities, taste/texture/match notes, and remarks. Cannot be undone.`)) return;
    try { await axios.delete(`${API}/rnd-chains/${chainId}/trials/${tid}`, { withCredentials: true }); toast.success(`${label} deleted`); load(); }
    catch { toast.error('Failed'); }
  };

  const pushChainTrialToRecipes = async (t) => {
    const label = `${chain?.name || 'Chain'} · ${t.label}`;
    if (!window.confirm(`Push trial "${t.label}" from chain "${chain?.name}" to main Recipes?`)) return;

    // Recipes are password-gated — ensure we have a valid token.
    let recipeToken = sessionStorage.getItem('ti_recipe_token') || '';
    if (!recipeToken) {
      const pw = window.prompt('Enter the Recipe password to push this trial into Recipes:');
      if (!pw) return;
      try {
        const res = await axios.post(`${API}/production/recipes/verify-password`,
          { password: pw }, { withCredentials: true });
        recipeToken = res.data?.token || pw;
        sessionStorage.setItem('ti_recipe_token', recipeToken);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Incorrect recipe password');
        return;
      }
    }

    try {
      const pid = (label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `chain-${t.id.slice(0,6)}`;
      // Convert ingredients → recipe.materials shape
      const ings = Array.isArray(t.ingredients) ? t.ingredients : [];
      const materials = ings.map(i => ({
        material_id: i.material_id || '',
        material_name: i.material_name || i.name || '',
        quantity_per_batch: parseFloat(i.quantity) || 0,
        unit: i.unit || 'kg',
      })).filter(m => m.material_name);
      // Auto-compute batch_size_kg from materials sum (kg-normalised). Falls back to
      // the trial's saved batch_size_kg only if the materials list is empty.
      const computedBatch = materials.reduce((s, m) => {
        const q = parseFloat(m.quantity_per_batch) || 0;
        const u = (m.unit || 'kg').toLowerCase();
        return s + (u === 'kg' ? q : u === 'g' ? q / 1000 : q);
      }, 0);
      await axios.post(`${API}/production/recipes`, {
        product_id: pid,
        product_name: label,
        product_code: t.product_code || chain?.product_code || '',
        batch_size_kg: computedBatch > 0 ? Math.round(computedBatch * 100) / 100 : (t.batch_size_kg || 0),
        notes: `From R&D Chain "${chain?.name}" trial ${t.label} — ${t.date || ''}`,
        materials,
      }, { withCredentials: true, headers: { 'X-Recipe-Token': recipeToken } });
      toast.success(`Pushed to Recipes with ${materials.length} ingredient(s)`);
    } catch (e) {
      if (e.response?.status === 401) sessionStorage.removeItem('ti_recipe_token');
      toast.error(e.response?.data?.detail || 'Failed to push to Recipes');
    }
  };

  if (!chain) return null;
  const trials = chain.trials || [];

  return (
    <Modal onClose={onClose} title={`${chain.name} — ${trials.length} trial${trials.length !== 1 ? 's' : ''}`} size="6xl" testid="chain-detail-modal">
      {/* Chain metadata */}
      <div className={`grid ${canViewPrices ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-2 text-[11px] mb-3`}>
        <MetaCard label="For" value={chain.for_client || '—'} />
        <MetaCard label="Goal" value={chain.goal || '—'} />
        <MetaCard label="Control" value={chain.control_sample || '—'} />
        {canViewPrices && <MetaCard label="Target ₹/kg" value={chain.target_cost_kg ? fmt(chain.target_cost_kg) : '—'} />}
      </div>

      {/* Trials side-by-side table */}
      {trials.length === 0 ? (
        <div className="bg-zinc-900 rounded-lg p-8 text-center text-zinc-500 text-sm">No trials yet. Add t1 to begin.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-zinc-800">
                <th className="text-left px-2 py-2 w-32">Ingredient</th>
                {trials.map(t => (
                  <th key={t.id} className="text-left px-2 py-2 min-w-[140px]">
                    <div className="flex items-center justify-between gap-1.5">
                      <div>
                        <div className="text-amber-300 font-semibold">{t.label}</div>
                        <div className="text-[9px] text-zinc-500 font-normal normal-case">{t.date}{canViewPrices ? ` · ₹${t.total_cost}` : ''}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => addTrial(t.label)}
                        title={`Clone as next version (auto-named t${trials.length + 1})`}
                        data-testid={`clone-trial-${t.id}`}
                        disabled={busy}
                        className="p-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/30 disabled:opacity-50">
                        <Copy size={10} />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <IngredientRows trials={trials} materials={materials} canViewPrices={canViewPrices} onUpdate={updateTrial} />
              {canViewPrices && (
                <tr className="border-t-2 border-zinc-700">
                  <td className="px-2 py-2 text-zinc-400 font-semibold">Total ₹</td>
                  {trials.map(t => <td key={t.id} className="px-2 py-2 text-emerald-300 font-mono font-bold">{fmt(t.total_cost)}</td>)}
                </tr>
              )}
              <tr>
                <td className="px-2 py-1 text-zinc-400 text-[10px] uppercase tracking-wider">Taste</td>
                {trials.map(t => <td key={t.id} className="px-2 py-1"><input value={t.taste_notes || ''} onChange={e => updateTrial(t.id, { taste_notes: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px]" placeholder="taste notes" /></td>)}
              </tr>
              <tr>
                <td className="px-2 py-1 text-zinc-400 text-[10px] uppercase tracking-wider">Texture</td>
                {trials.map(t => <td key={t.id} className="px-2 py-1"><input value={t.texture_notes || ''} onChange={e => updateTrial(t.id, { texture_notes: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px]" placeholder="texture" /></td>)}
              </tr>
              <tr>
                <td className="px-2 py-1 text-zinc-400 text-[10px] uppercase tracking-wider">Match /10</td>
                {trials.map(t => <td key={t.id} className="px-2 py-1"><input type="number" min="0" max="10" value={t.match_score || ''} onChange={e => updateTrial(t.id, { match_score: parseInt(e.target.value) || null })} className="w-16 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px] text-center" /></td>)}
              </tr>
              <tr>
                <td className="px-2 py-1 text-zinc-400 text-[10px] uppercase tracking-wider">Remarks</td>
                {trials.map((t, idx) => (
                  <td key={t.id} className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <input value={t.remarks || ''} onChange={e => updateTrial(t.id, { remarks: e.target.value })} className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px]" placeholder="remarks" />
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => updateTrial(t.id, { remarks: trials[idx - 1].remarks || '' })}
                          title={`Copy remarks from ${trials[idx - 1].label}`}
                          data-testid={`copy-remarks-${t.id}`}
                          className="p-0.5 rounded text-zinc-500 hover:text-amber-300">
                          <Copy size={9} />
                        </button>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td></td>
                {trials.map(t => (
                  <td key={t.id} className="px-2 py-1">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => pushChainTrialToRecipes(t)} data-testid={`trial-push-${t.id}`}
                        title="Push this trial to main Recipes"
                        className="p-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/30">
                        <FlaskConical size={11} />
                      </button>
                      <button onClick={() => delTrial(t.id)} data-testid={`trial-del-${t.id}`} className="text-red-400 hover:text-red-300"><Trash2 size={11} /></button>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        {trials.length > 0 && (
          <button onClick={() => addTrial(trials[trials.length - 1].label)} disabled={busy} data-testid="add-next-trial"
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg font-semibold">
            <Copy size={12} /> Clone last → t{trials.length + 1}
          </button>
        )}
        <button onClick={() => addTrial(null)} disabled={busy} data-testid="add-blank-trial"
          className="flex items-center gap-1.5 px-3 py-2 text-xs bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-semibold disabled:opacity-50">
          <Plus size={12} /> {busy ? 'Adding…' : `Add blank t${trials.length + 1}`}
        </button>
      </div>
    </Modal>
  );
}

function IngredientRows({ trials, materials, canViewPrices, onUpdate }) {
  // Build union of all ingredient names across trials + ability to add new
  const names = Array.from(new Set(trials.flatMap(t => (t.ingredients || []).map(i => i.name))));
  const [newIng, setNewIng] = useState('');

  const findIng = (t, name) => (t.ingredients || []).find(i => i.name === name);

  const setIng = (t, name, patch) => {
    const list = [...(t.ingredients || [])];
    const idx = list.findIndex(i => i.name === name);
    if (idx >= 0) list[idx] = { ...list[idx], ...patch };
    else list.push({ name, qty: 0, unit: 'g', rate: 0, product_code: '', ...patch });
    // drop zero-qty rows
    onUpdate(t.id, { ingredients: list.filter(i => i.name) });
  };

  const addNewIngredient = () => {
    if (!newIng.trim()) return;
    // Add to first trial with qty=0 so it shows in all columns
    if (trials[0]) setIng(trials[0], newIng.trim(), { qty: 0 });
    setNewIng('');
  };

  return (
    <>
      {names.map(name => {
        const ref = materials.find(m => m.name === name);
        return (
          <tr key={name} className="border-b border-zinc-800/30">
            <td className="px-2 py-1 text-zinc-200 text-[11px]">
              <div className="font-semibold">{name}</div>
              {ref?.product_code && <div className="text-[9px] text-zinc-500">{ref.product_code}</div>}
            </td>
            {trials.map(t => {
              const i = findIng(t, name) || {};
              return (
                <td key={t.id} className="px-2 py-1">
                  <div className="flex gap-1">
                    <input type="number" step="any" value={i.qty || ''} onChange={e => setIng(t, name, { qty: parseFloat(e.target.value) || 0, rate: i.rate || ref?.unit_price || 0, unit: i.unit || ref?.unit || 'g' })}
                      className="w-16 bg-zinc-950 border border-zinc-800 rounded px-1 py-0.5 text-[11px]" placeholder="qty" />
                    <select value={i.unit || ref?.unit || 'g'} onChange={e => setIng(t, name, { unit: e.target.value, qty: i.qty || 0, rate: i.rate || ref?.unit_price || 0 })}
                      className="bg-zinc-950 border border-zinc-800 rounded px-1 py-0.5 text-[11px]">
                      <option>g</option><option>kg</option><option>ml</option><option>L</option><option>unit</option>
                    </select>
                    {canViewPrices && (
                      <input type="number" step="any" value={i.rate || ''} onChange={e => setIng(t, name, { rate: parseFloat(e.target.value) || 0, qty: i.qty || 0, unit: i.unit || ref?.unit || 'g' })}
                        className="w-16 bg-zinc-950 border border-zinc-800 rounded px-1 py-0.5 text-[11px]" placeholder="₹/u" />
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      })}
      <tr>
        <td className="px-2 py-2">
          <div className="flex gap-1">
            <input value={newIng} onChange={e => setNewIng(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNewIngredient()}
              list="materials-dl" placeholder="+ ingredient"
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px]" data-testid="add-ingredient-input" />
            <datalist id="materials-dl">
              {materials.map(m => <option key={m.id} value={m.name} />)}
            </datalist>
            <button onClick={addNewIngredient} data-testid="add-ingredient-btn"
              className="text-amber-300 hover:text-amber-200"><Plus size={12} /></button>
          </div>
        </td>
        <td colSpan={trials.length}></td>
      </tr>
    </>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-2">
      <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-zinc-200 text-xs font-semibold truncate" title={value}>{value}</div>
    </div>
  );
}

function Modal({ children, title, onClose, testid, size = '2xl' }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-${size} max-h-[90vh] overflow-y-auto p-5 space-y-3`} onClick={e => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-zinc-50 text-sm">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', testid, required }) {
  return (
    <div>
      <label className="text-zinc-400 text-[11px] mb-1 block">{label}{required && <span className="text-amber-400"> *</span>}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
    </div>
  );
}
